import { useState } from 'react';
import { useLoading, makeSuccess, TResult, makeFailure } from "@sg-core/index";

import { ICompany, ICompanyFormData } from '../../models/ICompany';
import { useHprService } from '../../context/HprServiceContext';
import { useCompanyForm } from './useCompanyForm';
import { useCompanyCache } from './useCompanyCache';

interface IUseCreateCompanyData {
    companyId: ICompany["id"] | null;
    create: (values: ICompanyFormData) => Promise<TResult<ICompany>>;
    creating: boolean;
    createError?: string;
    createdAt?: Date;
}

export type IUseCreateCompanyHook = () => IUseCreateCompanyData;

export const useCreateCompanyHook: IUseCreateCompanyHook = () => {

    const {
        loading: creating,
        loadError: createError,
        startLoading,
        loadSuccess,
        loadErrored,
    } = useLoading();
    const service = useHprService();
    const validate = useCompanyForm().validate;
    const { revalidateList } = useCompanyCache()

    const [createdAt, setCreatedAt] = useState<Date>();
    const [companyId, setCompanyId] = useState<ICompany["id"] | null>(null);

    const create = async (values: ICompanyFormData): Promise<TResult<ICompany>> => {
        if (creating) {
            return makeFailure(Error("Create company prevented"))
        }

        try {

            const hasErrors = validate(values);
            if (hasErrors) throw Error("Validation errors")

            startLoading();
            const result = await service.createCompany(values);

            if (result.isSuccess) {
                setCompanyId(result.value.id);
                setCreatedAt(new Date());
                loadSuccess();

                revalidateList();
                return makeSuccess(result.value);
            };
            throw result.error;
        } catch (error) {
          //  console.info(error);
            loadErrored((error as Error).message);
            return makeFailure(error as Error);
        };
    };

    return {
        companyId,
        create,
        creating,
        createError,
        createdAt
    };
};
